import { useEffect, useState, useCallback, useRef } from 'react';
import { MapIcon } from '@schamburg-web/interfaces';
import { applyAllFilters } from './SearchUtil';
import { useModules } from '../contexts/ModulesContext/ModulesContext';
import { useSearch } from '../contexts/SearchContext/SearchContext';

/**
 * Custom hook for accessing module-specific Redux data without causing cross-module re-renders.
 * Each component using this hook will only re-render when ITS specific module data changes.
 */
export const useModuleData = <T extends MapIcon = MapIcon>(
  moduleName: string
) => {
  const { getStore, getModuleSlice, modulesEnabled, updateObject } =
    useModules();
  const { query } = useSearch();
  const [displayObjects, setDisplayObjects] = useState<MapIcon[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const displayObjectsRef = useRef<MapIcon[]>([]);

  const isEnabled = modulesEnabled[moduleName];

  // Update ref when displayObjects change
  useEffect(() => {
    displayObjectsRef.current = displayObjects;
  }, [displayObjects]);

  // Get current data from Redux
  const getCurrentData = useCallback(() => {
    const store = getStore();
    const moduleSlice = getModuleSlice(moduleName);

    if (!store || !moduleSlice || !isEnabled) {
      return { allObjects: [], displayObjects: [], isLoading: false };
    }

    const currentState = store.getState();
    return {
      allObjects: moduleSlice.selectors.selectAllObjects(currentState),
      displayObjects: moduleSlice.selectors.selectDisplayObjects(currentState),
      isLoading: moduleSlice.selectors.selectIsLoading(currentState),
    };
  }, [getStore, getModuleSlice, moduleName, isEnabled]);

  // Apply filters and update Redux
  const applyFilters = useCallback(
    (allObjects: MapIcon[], isInitialLoad = false) => {
      const store = getStore();
      const moduleSlice = getModuleSlice(moduleName);

      if (!store || !moduleSlice) return;

      // Prevent infinite loops during filter application
      if (isApplyingFiltersRef.current) {
        return;
      }

      isApplyingFiltersRef.current = true;

      try {
        // If query is empty or whitespace, return all objects without sanitization
        if (!query || query.trim() === '') {
          store.dispatch(moduleSlice.actions.setDisplayObjects(allObjects));
          setDisplayObjects([...allObjects]);
          return;
        }

        applyAllFilters(allObjects, query, (filteredObjects: MapIcon[]) => {
          store.dispatch(
            moduleSlice.actions.setDisplayObjects(filteredObjects)
          );

          // Always update local state directly to ensure immediate display
          setDisplayObjects([...filteredObjects]);
        });
      } finally {
        // Reset the flag after a brief delay to allow Redux updates to propagate
        setTimeout(() => {
          isApplyingFiltersRef.current = false;
        }, 10);
      }
    },
    [getStore, getModuleSlice, moduleName, query]
  );

  // Track if we're currently applying filters to prevent loops
  const isApplyingFiltersRef = useRef(false);

  // Add a debounce ref to prevent rapid successive updates
  const lastUpdateTimeRef = useRef(0);

  // Track previous allObjects to detect when they change
  const previousAllObjectsRef = useRef<MapIcon[]>([]);

  // Subscribe to store changes for this specific module only
  useEffect(() => {
    const store = getStore();
    const moduleSlice = getModuleSlice(moduleName);

    if (!store || !moduleSlice) {
      setDisplayObjects([]);
      return;
    }

    // Initial load function
    const performInitialLoad = () => {
      const { allObjects, displayObjects: currentDisplay } = getCurrentData();

      // If we have display objects, use them
      if (currentDisplay.length > 0) {
        setDisplayObjects([...currentDisplay]);
      } else if (allObjects.length > 0) {
        // If we have raw data but no filtered data, apply filters
        applyFilters(allObjects, true); // Pass true for initial load
      }
    };

    // Perform initial load
    performInitialLoad();

    // Subscribe to store changes
    const unsubscribe = store.subscribe(() => {
      // Debounce rapid updates (reduced to 5ms for testing)
      const now = Date.now();
      if (now - lastUpdateTimeRef.current < 5) {
        return;
      }
      lastUpdateTimeRef.current = now;
      const newState = store.getState();
      const newAllObjects = moduleSlice.selectors.selectAllObjects(newState);
      const newDisplayObjects =
        moduleSlice.selectors.selectDisplayObjects(newState);
      const newIsLoading = moduleSlice.selectors.selectIsLoading(newState);

      // Update loading state
      setIsLoading(newIsLoading);

      // Check if allObjects have changed (new data arrived)
      const previousAllObjects = previousAllObjectsRef.current;
      const allObjectsChanged =
        newAllObjects.length !== previousAllObjects.length ||
        newAllObjects.some((obj, index) => obj !== previousAllObjects[index]);

      if (allObjectsChanged) {
        previousAllObjectsRef.current = [...newAllObjects];

        // When allObjects change, re-apply filters to update displayObjects
        // Use a small delay to prevent rapid re-filtering
        setTimeout(() => {
          applyFilters(newAllObjects, false);
        }, 10);
        return; // Exit early to avoid double processing
      } // Check if display objects have actually changed
      const currentDisplayObjects = displayObjectsRef.current;

      // More robust comparison - check if the actual content has changed
      const hasContentChanged = () => {
        // First check lengths
        if (newDisplayObjects.length !== currentDisplayObjects.length) {
          return true;
        }

        // If lengths are same, check if objects or their properties have changed
        for (let i = 0; i < newDisplayObjects.length; i++) {
          const newObj = newDisplayObjects[i];
          const currentObj = currentDisplayObjects[i];

          // Check if objects exist
          if (!newObj || !currentObj) {
            return true;
          }

          // Check if IDs have changed (reordering)
          if (newObj.id !== currentObj.id) {
            return true;
          }

          // Check if object references have changed (indicates property updates)
          if (newObj !== currentObj) {
            return true;
          }

          // Additional check for key properties that commonly change in realtime
          const keyProperties = ['lastEvenntUpdate', 'lastUpdated'];

          for (const prop of keyProperties) {
            const newValue = (newObj as unknown as Record<string, unknown>)[
              prop
            ];
            const currentValue = (
              currentObj as unknown as Record<string, unknown>
            )[prop];
            if (newValue !== currentValue) {
              return true;
            }
          }
        }

        return false;
      };

      const contentChanged = hasContentChanged();

      if (contentChanged) {
        setDisplayObjects([...newDisplayObjects]);
      }

      // Handle case where new data arrives after initial load
      // If we have allObjects but no displayObjects, apply filters
      if (
        newAllObjects.length > 0 &&
        newDisplayObjects.length === 0 &&
        currentDisplayObjects.length === 0
      ) {
        applyFilters(newAllObjects, true); // Treat as initial load since no display objects exist
      }
    });
    return unsubscribe;
  }, [
    moduleName,
    getCurrentData,
    applyFilters,
    isEnabled,
    getStore,
    getModuleSlice,
  ]);

  // Create stable references for functions to avoid dependency issues
  const getCurrentDataRef = useRef(getCurrentData);
  const applyFiltersRef = useRef(applyFilters);

  // Update refs when functions change
  useEffect(() => {
    getCurrentDataRef.current = getCurrentData;
    applyFiltersRef.current = applyFilters;
  });

  // Re-apply filters when query changes
  useEffect(() => {
    const { allObjects } = getCurrentDataRef.current();
    if (allObjects.length > 0) {
      applyFiltersRef.current(allObjects, false); // Pass false for regular filter update
    }
  }, [query]); // Removed isEnabled from dependencies since layer visibility is handled elsewhere

  return {
    displayObjects: displayObjects as T[],
    isEnabled,
    isLoading,
    // Force a refresh of display objects (useful for debugging)
    refresh: () => {
      const { allObjects } = getCurrentData();
      if (allObjects.length > 0) {
        applyFilters(allObjects);
      }
    },
    // Test function to verify updateObject is working
    testUpdate: (objectId: string, updates: Partial<T>) => {
      if (updateObject) {
        updateObject(moduleName, objectId, updates);
      }
    },
    // Debug function to check current state
    debugState: () => {
      const {
        allObjects,
        displayObjects: currentDisplay,
        isLoading: loading,
      } = getCurrentData();
      return {
        allObjects,
        displayObjects: currentDisplay as T[],
        hookDisplayObjects: displayObjects as T[],
        isLoading: loading,
      };
    },
  };
};
